import { apiClient } from './client';

type ApiEnvelope<T> = {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
};

const unwrap = <T>(payload: unknown, fallback: string): T => {
  if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
    const envelope = payload as ApiEnvelope<T>;
    if (envelope.success) {
      return envelope.data as T;
    }

    const msg =
      (typeof envelope.error === 'string' && envelope.error.trim())
      || (typeof envelope.message === 'string' && envelope.message.trim())
      || fallback;
    throw new Error(msg);
  }

  return payload as T;
};

export interface EventWallModerationPost {
  id: number;
  guest_name: string;
  post_text: string;
  photo_url?: string;
  created_at: string;
  is_hidden: boolean;
}

export interface EventWallSettings {
  wall_preapprove_enabled: boolean;
  wall_post_event_window_days: number;
}

class EventWallApiService {
  async listPosts(celebrationSlug: string): Promise<EventWallModerationPost[]> {
    const response = await apiClient.get(`/celebrations/${celebrationSlug}/wall`);
    const payload = unwrap<EventWallModerationPost[]>(response.data, 'Failed to load wall posts');

    if (!Array.isArray(payload)) {
      return [];
    }

    return payload;
  }

  async setVisibility(celebrationSlug: string, postId: number, hidden: boolean): Promise<{ id: number; is_hidden: boolean }> {
    const response = await apiClient.patch(`/celebrations/${celebrationSlug}/wall/${postId}/visibility`, { hidden });
    return unwrap<{ id: number; is_hidden: boolean }>(response.data, 'Failed to update wall post visibility');
  }

  async getSettings(celebrationSlug: string): Promise<EventWallSettings> {
    const response = await apiClient.get(`/celebrations/${celebrationSlug}/wall/settings`);
    return unwrap<EventWallSettings>(response.data, 'Failed to load wall settings');
  }

  async updateSettings(
    celebrationSlug: string,
    input: Partial<EventWallSettings>,
  ): Promise<EventWallSettings> {
    const response = await apiClient.patch(`/celebrations/${celebrationSlug}/wall/settings`, input);
    return unwrap<EventWallSettings>(response.data, 'Failed to update wall settings');
  }
}

export const eventWallApiService = new EventWallApiService();
